import SchoolRepository from '@/core/infrastructure/repositories/school/core/SchoolRepository'
import { ModuleBase } from '@/presentation/configuration/modules/ModuleBase'
import FetchSchoolUseCase from '@/core/useCases/school/core/FetchSchoolUseCase'
import FetchSchoolsForMeUseCase from '@/core/useCases/school/core/FetchSchoolsForMeUseCase'
import FetchAllSchoolsUseCase from '@/core/useCases/school/core/FetchAllSchoolsUseCase'
import FetchSchoolDirectoryEntryUseCase from '@/core/useCases/school/core/FetchSchoolDirectoryEntryUseCase.ts'
import CreateSchoolUseCase from '@/core/useCases/school/core/CreateSchoolUseCase'
import CreateSchoolPeriodUseCase from '@/core/useCases/school/period/CreateSchoolPeriodUseCase'
import SchoolPeriodRepository from '@/core/infrastructure/repositories/school/period/SchoolPeriodRepository'
import FetchAllSchoolPeriodsUseCase from '@/core/useCases/school/period/FetchAllSchoolPeriodsUseCase'
import SchoolPeriodUserRepository from '@/core/infrastructure/repositories/school/user/SchoolPeriodUserRepository'
import { CreateSchoolPeriodUserUseCase } from '@/core/useCases/school/user/CreateSchoolPeriodUserUseCase'
import SchoolYearRepository from '@/core/infrastructure/repositories/school/year/SchoolYearRepository'
import FetchAllSchoolYearsUseCase from '@/core/useCases/school/year/FetchAllSchoolYearsUseCase'
import SchoolClassroomRepository from '@/core/infrastructure/repositories/school/classroom/SchoolClassroomRepository'
import FetchSchoolClassroomLoginHistoryUseCase from '@/core/useCases/dashboard/FetchSchoolClassroomLoginHistoryUseCase'
import { FetchSchoolClassroomUseCase } from '@/core/useCases/school/classroom/FetchSchoolClassroomUseCase'
import { FetchAllSchoolClassroomsUseCase } from '@/core/useCases/school/classroom/FetchAllSchoolClassroomsUseCase'
import FetchAllSchoolPeriodUsersUseCase from '@/core/useCases/school/user/FetchAllSchoolPeriodUsersUseCase'
import { CreateSchoolClassroomUseCase } from '@/core/useCases/school/classroom/CreateSchoolClassroomUseCase'
import PrepareSchoolPeriodUserEditUseCase from '@/core/useCases/school/user/PrepareSchoolPeriodUserEditUseCase'
import { UpdateSchoolPeriodUserUseCase } from '@/core/useCases/school/user/UpdateSchoolPeriodUserUseCase'
import FetchAvailableEducationalMembersUseCase from '@/core/useCases/school/user/FetchAvailableEducationalMembersUseCase'
import { AddExistingMembersToSchoolPeriodUseCase } from '@/core/useCases/school/user/AddExistingMembersToSchoolPeriodUseCase'
import { UpdateSchoolClassroomUsersUseCase } from '@/core/useCases/school/classroom/UpdateSchoolClassroomUsersUseCase'
import { UpdateSchoolClassroomUseCase } from '@/core/useCases/school/classroom/UpdateSchoolClassroomUseCase'
import { DeleteSchoolClassroomUseCase } from '@/core/useCases/school/classroom/DeleteSchoolClassroomUseCase'
import { CreateSchoolMonitoringGroupUseCase } from '@/core/useCases/school/group/CreateSchoolMonitoringGroupUseCase'
import { SchoolMonitoringGroupRepository } from '@/core/infrastructure/repositories/school/group/SchoolMonitoringGroupRepository'
import { UpdateSchoolMonitoringGroupUsersUseCase } from '@/core/useCases/school/group/UpdateSchoolMonitoringGroupUsersUseCase'
import { DeleteSchoolMonitoringGroupUseCase } from '@/core/useCases/school/group/DeleteSchoolMonitoringGroupUseCase'
import { UpdateSchoolUseCase } from '@/core/useCases/school/core/UpdateSchoolUseCase'
import AssignmentRepository from '@/core/infrastructure/repositories/school/assignment/AssignmentRepository'
import { FetchAllAssignmentsUseCase } from '@/core/useCases/school/assignment/FetchAllAssignmentsUseCase'
import { FetchValidModulesForAssignmentUseCase } from '@/core/useCases/school/assignment/FetchValidModulesForAssignmentUseCase'
import { CreateAssignmentUseCase } from '@/core/useCases/school/assignment/CreateAssignmentUseCase'
import { UpdateAssignmentUseCase } from '@/core/useCases/school/assignment/UpdateAssignmentUseCase'
import { SetModuleVisibilityUseCase } from '@/core/useCases/school/assignment/SetModuleVisibilityUseCase'
import { FetchAssignmentsForClassroomUseCase } from '@/core/useCases/school/assignment/FetchAssignmentsForClassroomUseCase'
import SchoolDirectoryRepository from '@/core/infrastructure/repositories/school/directory/SchoolDirectoryRepository.ts'
import { FetchSchoolPeriodUserByIdentifierUseCase } from '@/core/useCases/school/user/FetchSchoolPeriodUserByIdentifierUseCase.ts'
import { DeleteSchoolPeriodUserUseCase } from '@/core/useCases/school/user/DeleteSchoolPeriodUser.ts'

export class SchoolModule extends ModuleBase {
  initialize(): void {
    this.initializeCore()
    this.initializeDirectory()
    this.initializePeriod()
    this.initializeYear()
    this.initializeClassroom()
    this.initializeUser()
    this.initializeMonitoringGroup()
    this.initializeAssignment()
  }

  private initializeCore(): void {
    const schoolRepository = new SchoolRepository()
    this.registerRepository(SchoolRepository, schoolRepository)

    const fetchSchoolUseCase = new FetchSchoolUseCase(schoolRepository)
    const fetchSchoolForMeUseCase = new FetchSchoolsForMeUseCase(schoolRepository)
    const fetchAllSchoolsUseCase = new FetchAllSchoolsUseCase(schoolRepository)

    const createSchoolUseCase = new CreateSchoolUseCase(schoolRepository)
    const updateSchoolUseCase = new UpdateSchoolUseCase(schoolRepository)

    this.registerUseCase(FetchSchoolUseCase, fetchSchoolUseCase)
    this.registerUseCase(FetchSchoolsForMeUseCase, fetchSchoolForMeUseCase)
    this.registerUseCase(FetchAllSchoolsUseCase, fetchAllSchoolsUseCase)
    this.registerUseCase(CreateSchoolUseCase, createSchoolUseCase)
    this.registerUseCase(UpdateSchoolUseCase, updateSchoolUseCase)
  }

  private initializeDirectory(): void {
    const schoolDirectoryRepository = new SchoolDirectoryRepository()
    this.registerRepository(SchoolDirectoryRepository, schoolDirectoryRepository)

    const fetchSchoolDirectoryEntryUseCase = new FetchSchoolDirectoryEntryUseCase(
      schoolDirectoryRepository
    )

    this.registerUseCase(FetchSchoolDirectoryEntryUseCase, fetchSchoolDirectoryEntryUseCase)
  }

  private initializePeriod(): void {
    const schoolPeriodRepository = new SchoolPeriodRepository()
    this.registerRepository(SchoolPeriodRepository, schoolPeriodRepository)

    const fetchAllSchoolPeriodsUseCase = new FetchAllSchoolPeriodsUseCase(schoolPeriodRepository)
    const createSchoolPeriodUseCase = new CreateSchoolPeriodUseCase(schoolPeriodRepository)

    this.registerUseCase(FetchAllSchoolPeriodsUseCase, fetchAllSchoolPeriodsUseCase)
    this.registerUseCase(CreateSchoolPeriodUseCase, createSchoolPeriodUseCase)
  }

  private initializeYear(): void {
    const schoolYearRepository = new SchoolYearRepository()
    this.registerRepository(SchoolYearRepository, schoolYearRepository)

    const fetchAllSchoolYearsUseCase = new FetchAllSchoolYearsUseCase(schoolYearRepository)
    this.registerUseCase(FetchAllSchoolYearsUseCase, fetchAllSchoolYearsUseCase)
  }

  private initializeClassroom(): void {
    const schoolClassroomRepository = new SchoolClassroomRepository()
    this.registerRepository(SchoolClassroomRepository, schoolClassroomRepository)

    const fetchSchoolClassroomLoginHistoryUseCase = new FetchSchoolClassroomLoginHistoryUseCase(
      schoolClassroomRepository
    )
    const fetchSchoolClassroomUseCase = new FetchSchoolClassroomUseCase(schoolClassroomRepository)
    const fetchAllSchoolClassroomsUseCase = new FetchAllSchoolClassroomsUseCase(
      schoolClassroomRepository
    )
    const createSchoolClassroomUseCase = new CreateSchoolClassroomUseCase(schoolClassroomRepository)
    const updateSchoolClassroomUserUseCase = new UpdateSchoolClassroomUsersUseCase(
      schoolClassroomRepository
    )
    const updateSchoolClassroomUseCase = new UpdateSchoolClassroomUseCase(schoolClassroomRepository)
    const deleteSchoolClassroomUseCase = new DeleteSchoolClassroomUseCase(schoolClassroomRepository)

    this.registerUseCase(
      FetchSchoolClassroomLoginHistoryUseCase,
      fetchSchoolClassroomLoginHistoryUseCase
    )
    this.registerUseCase(FetchSchoolClassroomUseCase, fetchSchoolClassroomUseCase)
    this.registerUseCase(FetchAllSchoolClassroomsUseCase, fetchAllSchoolClassroomsUseCase)
    this.registerUseCase(CreateSchoolClassroomUseCase, createSchoolClassroomUseCase)
    this.registerUseCase(UpdateSchoolClassroomUsersUseCase, updateSchoolClassroomUserUseCase)
    this.registerUseCase(UpdateSchoolClassroomUseCase, updateSchoolClassroomUseCase)
    this.registerUseCase(DeleteSchoolClassroomUseCase, deleteSchoolClassroomUseCase)
  }

  private initializeUser(): void {
    const schoolPeriodUserRepository = new SchoolPeriodUserRepository()
    this.registerRepository(SchoolPeriodUserRepository, schoolPeriodUserRepository)

    const createSchoolPeriodUserUseCase = new CreateSchoolPeriodUserUseCase(
      schoolPeriodUserRepository
    )
    const prepareSchoolPeriodUserEditUseCase = new PrepareSchoolPeriodUserEditUseCase(
      schoolPeriodUserRepository
    )
    const fetchAllSchoolPeriodsUsersCase = new FetchAllSchoolPeriodUsersUseCase(
      schoolPeriodUserRepository
    )
    const updateSchoolPeriodUserUseCase = new UpdateSchoolPeriodUserUseCase(
      schoolPeriodUserRepository
    )
    const fetchAvailableEducationalMembersUseCase = new FetchAvailableEducationalMembersUseCase(
      schoolPeriodUserRepository
    )
    const addExistingMembersToSchoolPeriodUseCase = new AddExistingMembersToSchoolPeriodUseCase(
      schoolPeriodUserRepository
    )

    const fetchSchoolPeriodUserByIdentifierUseCase = new FetchSchoolPeriodUserByIdentifierUseCase(
      schoolPeriodUserRepository
    )

    const deleteSchoolPeriodUserByIdentifierUseCase = new DeleteSchoolPeriodUserUseCase(
      schoolPeriodUserRepository
    )

    this.registerUseCase(CreateSchoolPeriodUserUseCase, createSchoolPeriodUserUseCase)
    this.registerUseCase(PrepareSchoolPeriodUserEditUseCase, prepareSchoolPeriodUserEditUseCase)
    this.registerUseCase(FetchAllSchoolPeriodUsersUseCase, fetchAllSchoolPeriodsUsersCase)
    this.registerUseCase(UpdateSchoolPeriodUserUseCase, updateSchoolPeriodUserUseCase)
    this.registerUseCase(FetchAvailableEducationalMembersUseCase, fetchAvailableEducationalMembersUseCase)
    this.registerUseCase(AddExistingMembersToSchoolPeriodUseCase, addExistingMembersToSchoolPeriodUseCase)
    this.registerUseCase(FetchSchoolPeriodUserByIdentifierUseCase, fetchSchoolPeriodUserByIdentifierUseCase )
    this.registerUseCase(DeleteSchoolPeriodUserUseCase, deleteSchoolPeriodUserByIdentifierUseCase)
  }

  private initializeMonitoringGroup(): void {
    const schoolMonitoringGroupRepository = new SchoolMonitoringGroupRepository()

    this.registerRepository(SchoolMonitoringGroupRepository, schoolMonitoringGroupRepository)

    const createSchoolMonitoringGroupUseCase = new CreateSchoolMonitoringGroupUseCase(
      schoolMonitoringGroupRepository
    )
    const updateSchoolMonitoringGroupUsersUseCase = new UpdateSchoolMonitoringGroupUsersUseCase(
      schoolMonitoringGroupRepository
    )
    const deleteSchoolMonitoringGroupUseCase = new DeleteSchoolMonitoringGroupUseCase(
      schoolMonitoringGroupRepository
    )

    this.registerUseCase(CreateSchoolMonitoringGroupUseCase, createSchoolMonitoringGroupUseCase)
    this.registerUseCase(
      UpdateSchoolMonitoringGroupUsersUseCase,
      updateSchoolMonitoringGroupUsersUseCase
    )
    this.registerUseCase(DeleteSchoolMonitoringGroupUseCase, deleteSchoolMonitoringGroupUseCase)
  }

  private initializeAssignment(): void {
    const assignmentRepository = new AssignmentRepository()

    this.registerRepository(AssignmentRepository, assignmentRepository)

    const fetchAllAssignmentsUseCase = new FetchAllAssignmentsUseCase(assignmentRepository)
    const fetchValidModulesForAssignmentUseCase = new FetchValidModulesForAssignmentUseCase(
      assignmentRepository
    )
    const createAssignmentUseCase = new CreateAssignmentUseCase(assignmentRepository)
    const updateAssignmentUseCase = new UpdateAssignmentUseCase(assignmentRepository)
    const setModuleVisibilityUseCase = new SetModuleVisibilityUseCase(assignmentRepository)
    const fetchAssignmentsForClassroomUseCase = new FetchAssignmentsForClassroomUseCase(
      assignmentRepository
    )

    this.registerUseCase(FetchAllAssignmentsUseCase, fetchAllAssignmentsUseCase)
    this.registerUseCase(
      FetchValidModulesForAssignmentUseCase,
      fetchValidModulesForAssignmentUseCase
    )
    this.registerUseCase(CreateAssignmentUseCase, createAssignmentUseCase)
    this.registerUseCase(UpdateAssignmentUseCase, updateAssignmentUseCase)
    this.registerUseCase(SetModuleVisibilityUseCase, setModuleVisibilityUseCase)
    this.registerUseCase(FetchAssignmentsForClassroomUseCase, fetchAssignmentsForClassroomUseCase)
  }
}
